import { Mail, Phone, MapPin, User, EyeOff, AlertTriangle } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { getLanguage } from "@/utils/languageUtils";
import { leadUILabels, commonMiscLabels } from "@/i18n";

interface LeadContactInfoProps {
  leadId: string;
  customerName: string;
  email: string;
  phone: string;
  postalCode: string;
  isGuest?: boolean;
  hasOwnerInfo?: boolean; // New prop to indicate if owner info is missing
  onMaskLead?: (leadId: string) => Promise<void>;
  userPosition?: string | null; // Customer location/position from valuation form
  labelOverrides?: {
    postalCode?: string;
  };
}

export const LeadContactInfo = ({
  leadId,
  customerName,
  email,
  phone,
  postalCode,
  isGuest = false,
  hasOwnerInfo = true, // Default to true (has owner info)
  onMaskLead,
  userPosition,
  labelOverrides = {},
}: LeadContactInfoProps) => {
  // Check if data is already masked (check for asterisks pattern to be flexible with backend changes)
  const isMasked =
    email?.includes("***") ||
    phone?.includes("***") ||
    customerName?.includes("***");

  const uiLabels = leadUILabels(getLanguage());
  const commonLabels = commonMiscLabels(getLanguage());
  
  // If no owner info, show error state
  if (!hasOwnerInfo) {
    return (
      <Card className="shadow-md border dark:border-red-500 dark:bg-red-900/30 border-red-200 bg-red-50 transition-colors rounded-lg">
        <CardHeader className="p-3">
          <CardTitle className="flex items-center gap-1.5 text-sm text-red-700 dark:text-red-300">
            <AlertTriangle className="h-4 w-4" />
            {commonLabels.ownerInfoError}
          </CardTitle>
        </CardHeader>
        <CardContent className="p-4 pt-0">
          <div className="flex items-center gap-2 text-sm text-red-600 dark:text-red-300">
            <span>{commonLabels.noOwnerInfoAvailable}</span>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="shadow-md rounded-lg">
      <CardHeader className="p-3">
        <div className="flex items-center justify-between">
          <CardTitle className="flex items-center gap-1.5 text-sm">
            <User className="h-4 w-4" />
            {commonLabels.ownerInfo}
          </CardTitle>
          {isGuest && (
            <div className="flex items-center gap-2">
              <Badge
                variant="secondary"
                className="bg-yellow-100 text-yellow-800 border-yellow-200"
              >
                {commonLabels.guest}
              </Badge>
              {onMaskLead && !isMasked && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => onMaskLead(leadId)}
                  className="h-6 px-2 text-xs border dark:border-red-900 dark:bg-red-900/30 border-red-200 bg-red-50 transition-colors"
                >
                  <EyeOff className="h-3 w-3 mr-1" />
                  {commonLabels.maskInformation}
                </Button>
              )}
            </div>
          )}
        </div>
      </CardHeader>
      <CardContent className="p-4 pt-0 space-y-3">
        <div className="grid gap-3 text-sm">
          <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-2 sm:gap-4">
            <span className="text-muted-foreground">{commonLabels.name}</span>
            <span className="font-medium">{customerName}</span>
          </div>

          <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-2 sm:gap-4">
            <span className="text-muted-foreground">{commonLabels.email}</span>
            <a
              href={`mailto:${email}`}
              className="text-blue-500 hover:underline flex items-center gap-1"
            >
              <Mail className="h-3 w-3" />
              <span className="break-all">{email}</span>
            </a>
          </div>

          <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-2 sm:gap-4">
            <span className="text-muted-foreground">{commonLabels.phone}</span>
            <a
              href={`tel:${phone}`}
              className="text-blue-500 hover:underline flex items-center gap-1"
            >
              <Phone className="h-3 w-3" />
              {phone}
            </a>
          </div>

          {userPosition && (
            <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-2 sm:gap-4">
              <span className="text-muted-foreground">{uiLabels.vehicleLocationFromValuation || "Bilens plats (från värdering)"}</span>
              <span className="font-medium flex items-center gap-1">
                <MapPin className="h-3 w-3" />
                {userPosition}
              </span>
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
};
